import React, {useMemo, useState} from 'react'
import {Box, Dialog, LinearProgress, Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../store'
import {actions} from '../../store/vender/Auth/auth.slice'
import HomeApi from '../../Api/home'
import OnboardingStepGeneral from './OnboardingStepGeneral'
import OnboardingStepShipping from './OnboardingStepShipping'
import OnboardingStepRefund from './OnboardingStepRefund'
import OnboardingStepPayment from './OnboardingStepPayment'

type Props = {
  open: boolean
  onFinished: () => void
}

/** Phase 7 non-dismissible onboarding wizard. */
export default function OnboardingWizard({open, onFinished}: Props) {
  const {t, i18n} = useTranslation()
  const dispatch = useDispatch()
  const store = useSelector((state: RootState) => state.vender)
  const [step, setStep] = useState(0)
  const [transitioning, setTransitioning] = useState(false)

  const progress = useMemo(() => ((step + 1) / 4) * 100, [step])

  const refreshLunch = (then?: () => void) => {
    HomeApi.imagesSize((res: any) => {
      if (res?.data) {
        dispatch(actions.setImagesSize({...res.data}))
      }
      then?.()
    }, () => undefined)
  }

  const goNext = () => {
    setTransitioning(true)
    window.setTimeout(() => {
      setStep((s) => Math.min(s + 1, 3))
      setTransitioning(false)
    }, 350)
  }

  const afterStepSave = () => {
    refreshLunch(goNext)
  }

  const isRtl = store.isRtl !== 0 || i18n.language?.startsWith('ar')

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth='md'
      disableEscapeKeyDown
      onClose={() => undefined}
      dir={isRtl ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          m: {xs: 1, sm: 2},
          maxHeight: '92vh',
        },
      }}
    >
      <Box p={{xs: 2, sm: 3}}>
        <Stack gap={1} mb={2}>
          <Typography fontWeight={700} fontSize={22} color='#405189'>
            {t('onboarding.title') as string}
          </Typography>
          <Typography fontSize={14} color='text.secondary'>
            {t('onboarding.progress_label', {current: step + 1, total: 4}) as string}
          </Typography>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{
              height: 10,
              borderRadius: 99,
              backgroundColor: '#FEF0EC',
              '& .MuiLinearProgress-bar': {backgroundColor: '#F16645', borderRadius: 99},
            }}
          />
          {transitioning ? <LinearProgress sx={{height: 3, borderRadius: 99}} /> : null}
        </Stack>

        <Box sx={{opacity: transitioning ? 0.45 : 1, transition: 'opacity 250ms ease'}}>
          {step === 0 ? <OnboardingStepGeneral onSuccess={afterStepSave} /> : null}
          {step === 1 ? <OnboardingStepShipping onSuccess={afterStepSave} /> : null}
          {step === 2 ? (
            <OnboardingStepRefund onSuccess={afterStepSave} onSkip={afterStepSave} />
          ) : null}
          {step === 3 ? <OnboardingStepPayment onFinished={onFinished} /> : null}
        </Box>
      </Box>
    </Dialog>
  )
}
